import { createContext, useContext, useMemo, type ReactNode } from 'react';
import Select, { StylesConfig, components, OptionProps, SingleValueProps, MenuProps, Theme } from 'react-select';
import { User } from 'lucide-react';
import type { Member } from '@/types/member';
import type { MemberPhoto } from '@/types/photo';
import { useBulkMemberPhotos } from '@/hooks';
import { memberMatchesQuery } from '@/lib/member-search';
import { MemberSearchableDropdownProps as BaseDropdownProps } from '@/types/component-props';

export interface MemberOption {
  value: string;
  label: string;
  member: Member;
}

/** Row density. `large` is the global header search; everything else stays `default`. */
export type MemberOptionSize = 'default' | 'large';

interface MemberSearchableDropdownProps extends BaseDropdownProps {
  options: MemberOption[];
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  inputId?: string;
  /**
   * Row size for the result list. `large` renders a 56px thumbnail plus the
   * member's phone — used by the global header search, where seeing the face is
   * the point. Defaults to `default`, so existing call sites are unchanged.
   */
  optionSize?: MemberOptionSize;
  /**
   * Pre-fetched photos keyed by member id. When supplied, rows read from this
   * map instead of each firing their own query — the caller is expected to have
   * batched them into one `useBulkMemberPhotos` call. Without it every rendered
   * option fetches its own photo: fine for a handful of rows, expensive for
   * hundreds.
   */
  photos?: Record<string, MemberPhoto>;
  /** Rendered under the results, e.g. a "showing 8 of 23" truncation notice. */
  menuFooter?: ReactNode;
  /** Notified as the user types, so callers can filter/rank the options themselves. */
  onInputChange?: (value: string) => void;
  /** Message shown when nothing matches. */
  noOptionsMessage?: (input: { inputValue: string }) => string | null;
  /** Caps the visible list height; the rest scrolls. */
  maxMenuHeight?: number;
}

/**
 * Extras individual rows need, passed by context rather than through
 * `components={{ ... }}` closures: rebuilding those factories on each render
 * hands react-select a new component identity every keystroke, which remounts
 * every row — visible as thumbnail flicker and a reset scroll position.
 */
interface MemberDropdownExtras {
  photos?: Record<string, MemberPhoto>;
  menuFooter?: ReactNode;
}

const MemberDropdownExtrasContext = createContext<MemberDropdownExtras>({});

// Custom Option component with thumbnail
const MemberOptionRow = ({ size, ...props }: OptionProps<MemberOption> & { size: MemberOptionSize }) => {
  const { data } = props;
  const { photos } = useContext(MemberDropdownExtrasContext);

  // Hook order stays fixed — it's always called, but an empty id list disables
  // the query when the caller already handed us the photos.
  const memberIds = useMemo(
    () => (photos ? [] : [data.member.id]),
    [photos, data.member.id],
  );
  const { data: fetchedPhotos } = useBulkMemberPhotos(memberIds);
  const photo = photos?.[data.member.id] ?? fetchedPhotos?.[data.member.id];

  const isLarge = size === 'large';
  const fullName = `${data.member.first_name} ${data.member.last_name}`;
  // 150px thumbnail, not the 800px / ~200 KB `large`: these rows are 32-56px
  // circles. Fall back to `large` only if that one path failed to sign.
  const photoUrl = photo?.thumbnail_photo_url ?? photo?.large_photo_url ?? null;

  return (
    <components.Option {...props}>
      <div className={`flex items-center ${isLarge ? 'gap-4 py-1.5' : 'gap-3 py-1'}`}>
        <div className="flex-shrink-0">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={fullName}
              className={`${isLarge ? 'w-14 h-14' : 'w-8 h-8'} rounded-full object-cover`}
            />
          ) : (
            <div className={`${isLarge ? 'w-14 h-14' : 'w-8 h-8'} rounded-full bg-gray-300 flex items-center justify-center`}>
              <User className={`${isLarge ? 'w-7 h-7' : 'w-4 h-4'} text-gray-600`} />
            </div>
          )}
        </div>
        <div className="flex-grow min-w-0">
          <div className={`font-medium truncate ${isLarge ? 'text-base' : 'text-sm'}`}>
            {fullName}
          </div>
          <div className={`text-gray-500 truncate ${isLarge ? 'text-sm' : 'text-xs'}`}>
            {data.member.email}
          </div>
          {/* Phone only on the large rows: it's searchable now, so showing it
              explains why a phone query matched this member. */}
          {isLarge && data.member.whatsapp_number && (
            <div className="text-sm text-gray-500 truncate">
              {data.member.whatsapp_number}
            </div>
          )}
        </div>
      </div>
    </components.Option>
  );
};

// Module-level so react-select sees a stable component identity per size.
const CustomOption = (props: OptionProps<MemberOption>) => <MemberOptionRow {...props} size="default" />;
const CustomOptionLarge = (props: OptionProps<MemberOption>) => <MemberOptionRow {...props} size="large" />;

/**
 * The footer hangs off the menu container rather than the option list: the list
 * carries `role="listbox"`, whose children are supposed to be options, and a
 * notice inside it would be announced as one. Outside, it also stays pinned
 * instead of scrolling away with the results.
 */
const CustomMenu = (props: MenuProps<MemberOption>) => {
  const { menuFooter } = useContext(MemberDropdownExtrasContext);

  return (
    <components.Menu {...props}>
      {props.children}
      {menuFooter}
    </components.Menu>
  );
};

// Custom SingleValue component for selected item
const CustomSingleValue = (props: SingleValueProps<MemberOption>) => {
  const { data } = props;
  const memberIds = [data.member.id];
  const { data: memberPhotos = {} } = useBulkMemberPhotos(memberIds);
  const photo = memberPhotos[data.member.id];
  // 24px circle — see MemberOptionRow.
  const photoUrl = photo?.thumbnail_photo_url ?? photo?.large_photo_url ?? null;

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={`${data.member.first_name} ${data.member.last_name}`}
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
              <User className="w-3 h-3 text-gray-600" />
            </div>
          )}
        </div>
        <span className="truncate">
          {data.member.first_name} {data.member.last_name}
        </span>
      </div>
    </components.SingleValue>
  );
};

const customStyles: StylesConfig<MemberOption> = {
  control: (provided) => ({
    ...provided,
    borderColor: 'hsl(var(--input)) !important',
    boxShadow: 'none !important',
    borderRadius: 'var(--radius)',
    padding: '0.125rem 0.25rem',
    backgroundColor: 'hsl(var(--background))',
    minHeight: '36px',
    cursor: 'pointer',
    outline: 'none !important',
    '--tw-ring-offset-shadow': '0 0 #0000',
    '--tw-ring-shadow': '0 0 #0000',
    '--tw-ring-inset': ' ',
    '--tw-ring-offset-width': '0px',
    '--tw-ring-offset-color': 'transparent',
    '--tw-ring-color': 'transparent',
    '&:hover': {
      borderColor: 'hsl(var(--input)) !important',
      cursor: 'pointer',
      boxShadow: 'none !important',
      outline: 'none !important',
    },
    '&:focus': {
      borderColor: 'hsl(var(--input)) !important',
      boxShadow: 'none !important',
      outline: 'none !important',
      '--tw-ring-shadow': '0 0 #0000',
      '--tw-ring-offset-shadow': '0 0 #0000',
    },
    '&:focus-within': {
      borderColor: 'hsl(var(--input)) !important',
      boxShadow: 'none !important',
      outline: 'none !important',
      '--tw-ring-shadow': '0 0 #0000',
      '--tw-ring-offset-shadow': '0 0 #0000',
    },
    '&:active': {
      borderColor: 'hsl(var(--input)) !important',
      boxShadow: 'none !important',
      outline: 'none !important',
      '--tw-ring-shadow': '0 0 #0000',
      '--tw-ring-offset-shadow': '0 0 #0000',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    outline: 'none !important',
    boxShadow: 'none !important',
  }),
  input: (provided) => ({
    ...provided,
    color: 'hsl(var(--foreground))',
    outline: 'none !important',
    boxShadow: 'none !important',
    '--tw-ring-offset-shadow': '0 0 #0000',
    '--tw-ring-shadow': '0 0 #0000',
    '--tw-ring-inset': ' ',
    '--tw-ring-offset-width': '0px',
    '--tw-ring-offset-color': 'transparent',
    '--tw-ring-color': 'transparent',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'hsl(var(--background))',
    borderRadius: 'var(--radius)',
    border: '1px solid hsl(var(--input))',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    marginTop: '0.5rem',
    zIndex: 1000,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? 'hsl(220 13% 91%)' 
      : state.isFocused 
        ? 'hsl(var(--accent))' 
        : 'transparent',
    color: state.isSelected ? 'hsl(var(--foreground))' : 'hsl(var(--foreground))',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: 'hsl(220 13% 91%)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
    '&:hover': {
      color: 'hsl(var(--foreground))',
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
    '&:hover': {
      color: 'hsl(var(--foreground))',
    },
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

/**
 * The large rows carry a 56px thumbnail and three lines of text, which doesn't
 * fit the 320px control the header search hangs off — the menu inherits the
 * control's width by default, so widen it explicitly.
 */
const LARGE_MENU_MIN_WIDTH = 440;

const largeOptionStyles: StylesConfig<MemberOption> = {
  ...customStyles,
  menu: (provided, state) => ({
    ...(customStyles.menu?.(provided, state) ?? provided),
    minWidth: LARGE_MENU_MIN_WIDTH,
  }),
};

// Stable per size, so switching sizes doesn't rebuild the map on every render.
const DEFAULT_COMPONENTS = {
  Option: CustomOption,
  SingleValue: CustomSingleValue,
  Menu: CustomMenu,
};

const LARGE_COMPONENTS = {
  Option: CustomOptionLarge,
  SingleValue: CustomSingleValue,
  Menu: CustomMenu,
};

const customTheme = (theme: Theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'hsl(var(--primary))',
    primary25: 'hsl(var(--accent))',
    primary50: 'hsl(var(--accent))',
  },
});

export default function MemberSearchableDropdown({
  options,
  value,
  onChange,
  placeholder = "Search member...",
  className = "",
  isDisabled = false,
  isLoading = false,
  isClearable = true,
  isSearchable = true,
  inputId,
  optionSize = 'default',
  photos,
  menuFooter,
  onInputChange,
  noOptionsMessage,
  maxMenuHeight,
}: MemberSearchableDropdownProps) {
  // Map string value (member ID) to MemberOption
  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find(option => option.value === value) || null;
  }, [value, options]);

  // Adapter for react-select's onChange signature
  const handleChange = (option: MemberOption | null) => {
    onChange(option?.value || null);
  };

  const isLarge = optionSize === 'large';

  // Identity churn here only re-renders the rows; it never remounts them,
  // which is exactly why these travel by context instead of in `components`.
  const extras = useMemo<MemberDropdownExtras>(
    () => ({ photos, menuFooter }),
    [photos, menuFooter],
  );

  return (
    <MemberDropdownExtrasContext.Provider value={extras}>
      <div className={`no-focus-ring ${className}`}>
        <Select<MemberOption>
          inputId={inputId}
          options={options}
          value={selectedOption}
          onChange={handleChange}
          onInputChange={(input, meta) => {
            // Mirror what's actually in the box: react-select empties it on
            // blur and on menu close (which includes selecting), and a caller
            // holding a stale query would keep serving the old result list.
            if (meta.action === 'input-change') onInputChange?.(input);
            else if (meta.action === 'input-blur' || meta.action === 'menu-close') onInputChange?.('');
          }}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          styles={isLarge ? largeOptionStyles : customStyles}
          theme={customTheme}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          maxMenuHeight={maxMenuHeight}
          noOptionsMessage={noOptionsMessage}
          className="focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          classNamePrefix="member-select"
          components={isLarge ? LARGE_COMPONENTS : DEFAULT_COMPONENTS}
          filterOption={(option, inputValue) => memberMatchesQuery(option.data.member, inputValue)}
        />
      </div>
    </MemberDropdownExtrasContext.Provider>
  );
}
